import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";

import { categoryTree } from "../view_models/categoryTree";

// SVG Icons
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      viewBox="0 0 24 24"
      aria-labelledby="titleAccess-search"
      role="img"
      style={{ color: "#333333", fill: "none" }}
    >
      <path
        d="M.857 10.144a9.287 9.287 0 1 0 18.573 0 9.287 9.287 0 0 0-18.573 0ZM23.143 23.143l-6.434-6.434"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <title id="titleAccess-search">Search</title>
    </svg>
  );
}

function NotificationIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      viewBox="0 0 24 24"
      aria-labelledby="titleAccess-notification"
      role="img"
      style={{ color: "#333333", fill: "none", fontSize: "20px" }}
    >
      <path
        d="M9.171 20.133a2.71 2.71 0 0 0 2.622 2.028 2.71 2.71 0 0 0 2.622-2.028M12.15 2.015c1.864 0 3.652.768 4.97 2.136a7.43 7.43 0 0 1 2.059 5.157c0 8.11 2.85 8.327 3.621 8.327H1.5c.786 0 3.621-.235 3.621-8.327 0-1.934.74-3.79 2.059-5.157a6.9 6.9 0 0 1 4.97-2.136Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <title id="titleAccess-notification">Notification</title>
    </svg>
  );
}

function AccountIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      viewBox="0 0 24 24"
      aria-labelledby="titleAccess-account"
      role="img"
      style={{ color: "#333333", fill: "none", fontSize: "24px" }}
    >
      <path
        d="M8 6.857a4 4 0 1 0 8 0 4 4 0 0 0-8 0ZM21 20.437c-.617-1.743-1.82-3.263-3.437-4.341S14 14.437 12 14.437s-3.947.581-5.563 1.659C4.82 17.174 3.617 18.694 3 20.437z"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <title id="titleAccess-account">Account</title>
    </svg>
  );
}

function CartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      viewBox="0 0 24 24"
      aria-labelledby="titleAccess-cart"
      role="img"
      style={{ color: "#333333", fill: "none", fontSize: "20px" }}
    >
      <path
        d="M20.74 14.714H7.744L6.029 5.286h16.114a.857.857 0 0 1 .843 1.01l-1.402 7.714a.86.86 0 0 1-.843.704ZM6.029 5.286l-.72-3.6A.86.86 0 0 0 4.469 1H1.743M7.743 14.714l.72 3.6a.86.86 0 0 0 .84.686h9.583"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.884 22.429a.857.857 0 1 1-1.714 0 .857.857 0 0 1 1.714 0ZM11.17 22.429a.857.857 0 1 1-1.715 0 .857.857 0 0 1 1.715 0Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <title id="titleAccess-cart">Cart</title>
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 2L4 4L6 2"
        stroke="#333333"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Hamburger icon for Categories: two lines that animate to X when open; on hover lines spread (top up, bottom down) */
function HamburgerIcon({
  className,
  open,
  hovered,
}: {
  className?: string;
  open?: boolean;
  hovered?: boolean;
}) {
  // Closed state: lines separated vertically
  // Hover state: lines spread more
  // Open state: lines at center, rotated to form X
  const topLineY = open ? 0 : hovered ? -2.5 : -2;
  const bottomLineY = open ? 0 : hovered ? 2.5 : 2;

  return (
    <svg
      className={className}
      width="12"
      height="6"
      viewBox="0 0 12 6"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ overflow: "visible" }}
    >
      {/* Top line - starts at center, translates up when closed, rotates 45° when open */}
      <path
        d="M0 3h12"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        style={{
          transform: `translateY(${topLineY}px) rotate(${open ? 45 : 0}deg)`,
          transformOrigin: "6px 3px",
          transition: "transform 0.2s ease",
        }}
      />
      {/* Bottom line - starts at center, translates down when closed, rotates -45° when open */}
      <path
        d="M0 3h12"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        style={{
          transform: `translateY(${bottomLineY}px) rotate(${open ? -45 : 0}deg)`,
          transformOrigin: "6px 3px",
          transition: "transform 0.2s ease",
        }}
      />
    </svg>
  );
}

interface NavTab {
  label: string;
  path: string;
  isActive?: boolean;
}

type TrendsHubNavProps = {
  /** When true, tab links use justify-center (centered). */
  tabsCentered?: boolean;
  /** When true, show a gray border below the global nav (below the tabs row). */
  borderBelowNav?: boolean;
};

export default function TrendsHubNav({
  tabsCentered = false,
  borderBelowNav = false,
}: TrendsHubNavProps = {}) {
  const history = useHistory();
  const location = useLocation();
  const tabsRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLAnchorElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const underlineRef = useRef<HTMLDivElement>(null);
  const underlineInitializedRef = useRef(false);
  const categoryMenuWasOpenRef = useRef(false);
  const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const UNDERLINE_TRANSITION =
    "left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [categoriesHovered, setCategoriesHovered] = useState(false);
  const [hoveredC1Id, setHoveredC1Id] = useState<string | null>(null);
  const [hoveredC2Id, setHoveredC2Id] = useState<string | null>(null);
  const navWrapperRef = useRef<HTMLDivElement>(null);
  const navStickyRef = useRef<HTMLDivElement>(null);
  const [navHeight, setNavHeight] = useState(0);

  // Close category menu when clicking outside; reset hover state when menu closes
  useEffect(() => {
    if (!categoryMenuOpen) {
      setHoveredC1Id(null);
      setHoveredC2Id(null);
      return;
    }
    const handleClick = (e: MouseEvent) => {
      if (
        navWrapperRef.current &&
        !navWrapperRef.current.contains(e.target as Node)
      ) {
        setCategoryMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [categoryMenuOpen]);

  // Define nav tabs - each links to a page (placeholder or real) for prototyping global nav interaction
  const isForYouActive =
    location.pathname === "/" ||
    location.pathname === "/homepage" ||
    location.pathname === "/global-navigation";
  const navTabs: NavTab[] = [
    {
      label: "Browse all",
      path: "/categories",
      isActive: location.pathname === "/categories",
    },
    { label: "For you", path: "/global-navigation", isActive: isForYouActive },
    {
      label: "Ideas Hub",
      path: "/ideas-hub",
      isActive: location.pathname === "/ideas-hub",
    },
    {
      label: "Local",
      path: "/local",
      isActive: location.pathname === "/local",
    },
    {
      label: "Brand updates",
      path: "/brand-updates",
      isActive: location.pathname === "/brand-updates",
    },
    {
      label: "Bestsellers",
      path: "/bestsellers",
      isActive: location.pathname === "/bestsellers",
    },
    {
      label: "New products",
      path: "/new-products",
      isActive: location.pathname === "/new-products",
    },
    {
      label: "New brands",
      path: "/new-brands",
      isActive: location.pathname === "/new-brands",
    },
    {
      label: "Now on Faire",
      path: "/now-on-faire",
      isActive: location.pathname === "/now-on-faire",
    },
    {
      label: "Home decor",
      path: "/home-decor",
      isActive: location.pathname === "/home-decor",
    },
    {
      label: "Food & beverage",
      path: "/food-beverage",
      isActive: location.pathname === "/food-beverage",
    },
    {
      label: "Paper & novelty",
      path: "/paper-novelty",
      isActive: location.pathname === "/paper-novelty",
    },
    { label: "Sale", path: "/sale", isActive: location.pathname === "/sale" },
  ];

  // Check scrollability (hide right arrow when at far end; small epsilon for subpixel/rounding)
  const checkScrollability = () => {
    if (!tabsRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
    const maxScrollLeft = scrollWidth - clientWidth;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft < maxScrollLeft - 2);
  };

  // Resolve which path is "active" for finding the DOM node (For you = /global-navigation)
  const activePath =
    location.pathname === "/" || location.pathname === "/global-navigation"
      ? "/global-navigation"
      : location.pathname;

  // Update underline position so it sticks to the active tab.
  // Find active link by data attribute so we always target the current route's tab (avoids ref timing).
  // Use getBoundingClientRect so position is correct regardless of scroll/padding; both in viewport coords.
  const updateUnderline = () => {
    if (!tabsRef.current || !underlineRef.current) return;
    const container = tabsRef.current;
    const activeLink = container.querySelector<HTMLAnchorElement>(
      `[data-nav-path="${activePath}"]`
    );
    if (!activeLink) return;
    const tabRect = activeLink.getBoundingClientRect();
    // Underline is in the wrapper for both variants; position relative to wrapper
    const refRect = underlineRef.current.offsetParent
      ? (
          underlineRef.current.offsetParent as HTMLElement
        ).getBoundingClientRect()
      : container.getBoundingClientRect();
    const left = tabRect.left - refRect.left;
    const width = tabRect.width;
    underlineRef.current.style.left = `${left}px`;
    underlineRef.current.style.width = `${width}px`;
  };

  useLayoutEffect(() => {
    if (categoryMenuOpen) {
      categoryMenuWasOpenRef.current = true;
    }
  }, [categoryMenuOpen]);

  useLayoutEffect(() => {
    if (!underlineRef.current) {
      checkScrollability();
      return;
    }
    const isFirstUpdate = !underlineInitializedRef.current;
    const justClosedMenu = !categoryMenuOpen && categoryMenuWasOpenRef.current;
    if (isFirstUpdate || justClosedMenu) {
      if (justClosedMenu) categoryMenuWasOpenRef.current = false;
      underlineRef.current.style.transition = "none";
    }
    updateUnderline();
    if (isFirstUpdate) {
      underlineInitializedRef.current = true;
    }
    if (isFirstUpdate || justClosedMenu) {
      const el = underlineRef.current;
      requestAnimationFrame(() => {
        if (el) el.style.transition = UNDERLINE_TRANSITION;
      });
    }
    checkScrollability();
  }, [location.pathname, activePath, categoryMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      checkScrollability();
      if (underlineRef.current) {
        underlineRef.current.style.transition = "none";
      }
      updateUnderline();
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
      scrollEndTimerRef.current = setTimeout(() => {
        if (underlineRef.current) {
          underlineRef.current.style.transition = UNDERLINE_TRANSITION;
        }
        scrollEndTimerRef.current = null;
      }, 100);
    };
    const handleResize = () => {
      checkScrollability();
      updateUnderline();
    };
    const container = tabsRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleResize);
      return () => {
        container.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
        if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
      };
    }
  }, []);

  const handleTabClick = (path: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (path !== "#") {
      history.push({ pathname: path, search: location.search });
    }
  };

  const scrollTabs = (direction: "left" | "right") => {
    if (!tabsRef.current) return;
    const scrollAmount = 200;
    tabsRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
    // Recheck after smooth scroll ends so right arrow hides when at far end
    setTimeout(checkScrollability, 400);
  };

  return (
    <div ref={navWrapperRef} className="relative">
      <div
        ref={navStickyRef}
        className={cn(
          "bg-fs-surface-primary sticky top-0 z-1000",
          borderBelowNav ? "border-b border-[#dfe0e1]" : ""
        )}
        style={{ width: "100vw", position: "sticky", left: 0, right: 0 }}
      >
        {/* Top Nav - same spacing for both: content height + pt-4 pb-0 so 16px gap to links */}
        <div className="w-full px-12">
          <div className="flex items-center gap-4 pt-4 pb-0">
            {/* Faire Logo - 104px width, 25px height */}
            <div className="h-[25px] w-[104px] shrink-0">
              <button
                onClick={() => history.push("/")}
                className="relative h-full w-full"
                aria-label="Go to Faire homepage"
              >
                <img
                  src="/images/faire-logo.svg"
                  alt="Faire Logo"
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "https://cdn.faire.com/static/logo.svg";
                  }}
                />
              </button>
            </div>

            {/* Search Bar - Flex-1 */}
            <div className="flex min-w-0 flex-1 items-center justify-center">
              <div className="flex h-12 w-full items-center gap-2 rounded-full bg-[#f7f7f7] px-4 pr-5">
                <SearchIcon className="h-4 w-4 shrink-0" />
                <input
                  type="text"
                  placeholder="Search for products or brands"
                  className="min-w-0 flex-1 border-none bg-transparent text-sm text-[#757575] outline-hidden placeholder:text-[#757575]"
                  style={{
                    fontFamily:
                      "var(--slate-typography-family-sans-serif, 'Graphik', sans-serif)",
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.15px",
                  }}
                />
              </div>
            </div>

            {/* Icon Buttons - 8px gap */}
            <div className="flex shrink-0 items-center gap-2">
              <button className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-[#f7f7f7]">
                <NotificationIcon className="h-5 w-5" />
              </button>
              <button className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-[#f7f7f7]">
                <AccountIcon className="h-5 w-5" />
              </button>
              <button className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-[#f7f7f7]">
                <CartIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Nav - same spacing for both: 16px above links, 16px below, underline on border */}
        <div className="relative w-full px-12 pt-4 pb-4">
          <div className="relative h-[28px]">
            {/* Scroll buttons: gradient fade + button on top of tabs (z-[50] above link z-20); FauxDS Pagination arrow style */}
            {canScrollLeft && (
              <div className="absolute top-1/2 left-0 z-50 flex h-[28px] -translate-y-1/2 items-center">
                <button
                  onClick={() => scrollTabs("left")}
                  className="bg-fs-surface-primary flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[var(--slate-radius-component-default,4px)] text-[var(--slate-color-action-icon-default,#333333)] transition-colors duration-150 hover:bg-[var(--slate-color-action-surface-inverse-hover)] hover:text-[var(--slate-color-action-icon-default,#333333)] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2"
                  aria-label="Scroll left"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <div
                  className="bg-fs-surface-primary pointer-events-none h-full w-[12px] min-w-[12px] shrink-0"
                  style={{
                    background:
                      "linear-gradient(to right, #ffffff, rgba(255,255,255,0))",
                  }}
                  aria-hidden
                />
              </div>
            )}
            {canScrollRight && (
              <div className="absolute top-1/2 right-0 z-50 flex h-[28px] -translate-y-1/2 items-center">
                <div
                  className="pointer-events-none h-full w-[12px] min-w-[12px] shrink-0"
                  style={{
                    background:
                      "linear-gradient(to right, rgba(255,255,255,0), #ffffff)",
                  }}
                  aria-hidden
                />
                <button
                  onClick={() => scrollTabs("right")}
                  className="bg-fs-surface-primary flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[var(--slate-radius-component-default,4px)] text-[var(--slate-color-action-icon-default,#333333)] transition-colors duration-150 hover:bg-[var(--slate-color-action-surface-inverse-hover)] hover:text-[var(--slate-color-action-icon-default,#333333)] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2"
                  aria-label="Scroll right"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            )}

            {/* Scrollable Tabs Container - padding reserves space so gradient/button overlay the fade zone */}
            <div
              ref={tabsRef}
              className={cn(
                "scrollbar-hide relative z-0 flex h-full items-center gap-8 overflow-x-auto",
                tabsCentered ? "justify-center" : "justify-start"
              )}
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                paddingLeft: canScrollLeft ? 44 : 0,
                paddingRight: canScrollRight ? 44 : 0,
              }}
            >
              {navTabs.map((tab, index) => {
                if (tab.label === "Browse all") {
                  return (
                    <button
                      key={index}
                      type="button"
                      onMouseEnter={() => setCategoriesHovered(true)}
                      onMouseLeave={() => setCategoriesHovered(false)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const willOpen = !categoryMenuOpen;
                        if (willOpen && navStickyRef.current)
                          setNavHeight(navStickyRef.current.offsetHeight);
                        setCategoryMenuOpen(willOpen);
                      }}
                      aria-expanded={categoryMenuOpen}
                      aria-haspopup="menu"
                      className="text-fs-text-primary relative z-20 flex cursor-pointer items-center gap-2 rounded-[var(--slate-radius-component-default,4px)] px-0 py-1 text-sm leading-5 font-normal tracking-[0.15px] whitespace-nowrap transition-colors hover:text-[#000000]"
                      style={{
                        fontFamily:
                          "var(--slate-typography-family-sans-serif, 'Graphik', sans-serif)",
                      }}
                    >
                      <HamburgerIcon
                        className="text-fs-text-primary h-[6px] w-3 shrink-0"
                        open={categoryMenuOpen}
                        hovered={categoriesHovered}
                      />
                      {tab.label}
                    </button>
                  );
                }
                if (categoryMenuOpen && !tabsCentered) {
                  /* Left-aligned: invisible placeholder keeps layout so Categories button doesn't move */
                  return (
                    <span
                      key={index}
                      className="pointer-events-none invisible flex items-center gap-2 px-0 py-1 text-sm leading-5 tracking-[0.15px] whitespace-nowrap"
                      aria-hidden
                    >
                      {tab.label}
                    </span>
                  );
                }
                return (
                  <Link
                    key={index}
                    to={{ pathname: tab.path, search: location.search }}
                    data-nav-path={tab.path}
                    ref={tab.isActive ? activeTabRef : null}
                    onClick={(e) => {
                      e.preventDefault();
                      history.push({
                        pathname: tab.path,
                        search: location.search,
                      });
                    }}
                    className={cn(
                      "relative z-20 flex cursor-pointer items-center gap-2 px-0 py-1 text-sm leading-5 tracking-[0.15px] whitespace-nowrap no-underline transition-colors",
                      tab.isActive
                        ? "text-fs-text-primary font-normal"
                        : tab.label === "Sale"
                          ? "font-normal text-[#7d3e1e] hover:text-[#6b3419]"
                          : "text-fs-text-primary font-normal hover:text-[#000000]"
                    )}
                    style={{
                      fontFamily:
                        "var(--slate-typography-family-sans-serif, 'Graphik', sans-serif)",
                      pointerEvents: "auto",
                    }}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </div>
          </div>
          {/* Underline at bottom of wrapper (on border) for both variants */}
          {!categoryMenuOpen && navTabs.some((tab) => tab.isActive) && (
            <div
              ref={underlineRef}
              className="pointer-events-none absolute bottom-0 h-[2px] bg-[#333333]"
              style={{
                left: 0,
                width: 0,
                transition: UNDERLINE_TRANSITION,
              }}
            />
          )}
        </div>

        <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      </div>

      {/* Category menu dropdown - three columns, hover C1 shows C2, hover C2 shows C3 (matches category-menu-styling) */}
      {categoryMenuOpen && (
        <>
          <button
            type="button"
            aria-label="Close category menu"
            className="fixed right-0 bottom-0 left-0 z-1001 cursor-default bg-(--slate-color-grey-1000)/30"
            style={{ top: navHeight }}
            onClick={() => setCategoryMenuOpen(false)}
          />
          <div
            className="bg-fs-surface-primary fixed right-0 left-0 z-1002 shadow-lg"
            style={{ top: navHeight, width: "100vw" }}
          >
            <div
              className="group flex w-full overflow-hidden px-12"
              onMouseLeave={() => {
                setHoveredC1Id(null);
                setHoveredC2Id(null);
              }}
              style={{
                fontFamily:
                  "var(--slate-typography-family-sans-serif, 'Graphik', sans-serif)",
              }}
            >
              {/* Column 1: C1 categories — 22px (same as category-menu-styling first level) */}
              <div className="flex min-w-[220px] flex-col py-6 pr-10">
                <div className="pb-3 text-[12px] leading-4 font-normal text-[#757575]">
                  Browse all
                </div>
                <nav
                  className="flex flex-col items-start gap-0"
                  aria-label="Category navigation"
                >
                  {categoryTree.map((c1, index) => (
                    <Link
                      key={c1.id}
                      to={{
                        pathname: c1.href ?? "/categories",
                        search: location.search,
                      }}
                      onClick={() => setCategoryMenuOpen(false)}
                      className={cn(
                        "animate-category-link-in inline-block py-1 text-[22px] leading-[26px] whitespace-nowrap opacity-0 transition-colors duration-150 hover:text-(--slate-color-grey-1000)! hover:underline focus:text-(--slate-color-grey-1000)! focus:underline focus:outline-hidden",
                        hoveredC1Id === c1.id
                          ? "text-(--slate-color-grey-1000)! underline"
                          : hoveredC1Id
                            ? "text-[#757575]"
                            : "text-(--slate-color-grey-1000)"
                      )}
                      style={{ animationDelay: `${index * 14}ms` }}
                      onMouseEnter={() => setHoveredC1Id(c1.id)}
                    >
                      {c1.label}
                    </Link>
                  ))}
                </nav>
              </div>
              {/* Column 2: C2 for hovered C1 */}
              {(() => {
                const hoveredC1 = hoveredC1Id
                  ? categoryTree.find((c) => c.id === hoveredC1Id)
                  : null;
                if (!hoveredC1?.children?.length) return null;
                return (
                  <div className="flex min-w-[220px] flex-col py-6 pr-10 pl-10">
                    <div className="pb-3 text-[12px] leading-4 font-normal text-[#757575]">
                      {hoveredC1.label}
                    </div>
                    <nav
                      className="flex flex-col items-start gap-0"
                      aria-label={`${hoveredC1.label} subcategories`}
                    >
                      {hoveredC1.children.map((c2) => (
                        <Link
                          key={c2.id}
                          to={{
                            pathname: c2.href ?? "/categories",
                            search: location.search,
                          }}
                          onClick={() => setCategoryMenuOpen(false)}
                          className={cn(
                            "inline-block py-0.5 text-[18px] leading-[26px] whitespace-nowrap text-(--slate-color-grey-1000) transition-colors duration-150 group-hover:text-[#757575] hover:text-(--slate-color-grey-1000)! hover:underline focus:text-(--slate-color-grey-1000)! focus:underline focus:outline-hidden",
                            hoveredC2Id === c2.id
                              ? "text-(--slate-color-grey-1000)! underline"
                              : ""
                          )}
                          onMouseEnter={() => setHoveredC2Id(c2.id)}
                        >
                          {c2.label}
                        </Link>
                      ))}
                    </nav>
                  </div>
                );
              })()}
              {/* Column 3: C3 for hovered C2 */}
              {(() => {
                const hoveredC1 = hoveredC1Id
                  ? categoryTree.find((c) => c.id === hoveredC1Id)
                  : null;
                const hoveredC2 = hoveredC1?.children?.find(
                  (c) => c.id === hoveredC2Id
                );
                if (!hoveredC2?.children?.length) return null;
                return (
                  <div className="flex min-w-[260px] flex-col py-6 pl-10">
                    <div className="pb-3 text-[12px] leading-4 font-normal text-[#757575]">
                      {hoveredC2.label}
                    </div>
                    <nav
                      className="flex flex-col items-start gap-0"
                      aria-label={`${hoveredC2.label} subcategories`}
                    >
                      {hoveredC2.children.map((c3) => (
                        <Link
                          key={c3.id}
                          to={{
                            pathname: c3.href ?? "/categories",
                            search: location.search,
                          }}
                          onClick={() => setCategoryMenuOpen(false)}
                          className="inline-block py-0.5 text-[18px] leading-[26px] whitespace-nowrap text-(--slate-color-grey-1000) transition-colors duration-150 group-hover:text-[#757575] hover:text-(--slate-color-grey-1000)! hover:underline focus:text-(--slate-color-grey-1000)! focus:underline focus:outline-hidden"
                        >
                          {c3.label}
                        </Link>
                      ))}
                    </nav>
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
